import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-text-field>` says about itself.
 *
 * The piece is thin on purpose: it is `CaosBaseField` with the inner input's type set to `text`,
 * so everything below except this sentence is the shared field contract. It is declared here in
 * full rather than by pointing at BaseField, because a person reading TextField's page is asking
 * what TEXTFIELD takes and should not have to follow a link to find out.
 *
 * TWO THINGS THE SOURCE SETTLES THAT A NAME WOULD NOT.
 *
 * `min`, `max` and `step` are in the shared `observedAttributes` list and are forwarded to the
 * inner input by the shared `sync()`, so they are observed here too — but the inner input is
 * `type="text"`, which ignores all three. They are NOT listed as inputs below, because an input
 * table is a list of what setting a value DOES, and setting these does nothing. They are real on
 * NumberField and on DateField, and that is where they are documented.
 *
 * `inline` is honoured through the stylesheet (`:host([inline])`) rather than through
 * `attributeChangedCallback`, so it is not in `observedAttributes` and does not appear there when
 * somebody greps. It is a real input all the same — setting it switches the whole rendering — so
 * it is listed.
 */
const docs: ComponentDocs = {
  aliases: ['text input', 'textarea', 'multiline text', 'long text', 'input', 'textbox', 'string field', 'form field'],

  answers: [
    'let somebody type a short piece of text',
    'put a labelled box on a form',
    'show a field as refused, with the reason under it',
    'ask for a name, a reference or a description on one line',
  ],

  placements: ['recordPage', 'appPage', 'screen'],

  inputs: [
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        "The field's name, drawn above the control and pointed at the input, so clicking it focuses " +
        'the box. Left unset, no label is drawn at all.',
    },
    {
      name: 'placeholder',
      attribute: 'placeholder',
      type: 'text',
      description: 'The greyed hint inside an empty box. A hint, never a substitute for the label.',
    },
    {
      name: 'value',
      attribute: 'value',
      type: 'text',
      description:
        'The current text. It is written back to this attribute on every keystroke, so the attribute ' +
        'and what is on screen stay the same thing.',
    },
    {
      name: 'name',
      attribute: 'name',
      type: 'text',
      description: 'The form name forwarded to the inner input, for a field submitted as part of a form.',
    },
    {
      name: 'required',
      attribute: 'required',
      type: 'boolean',
      description:
        'This field has to be filled in. Draws the asterisk beside the label and marks the inner ' +
        'input required.',
      default: false,
    },
    {
      name: 'disabled',
      attribute: 'disabled',
      type: 'boolean',
      description: 'You may not type in this. Greys the box and refuses focus.',
      default: false,
    },
    {
      name: 'invalid',
      attribute: 'invalid',
      type: 'boolean',
      description:
        'What is in the box is wrong. Turns the border and the message red and marks the input invalid ' +
        'for a screen reader. It does not itself decide anything is wrong — the page sets it.',
      default: false,
    },
    {
      name: 'message',
      attribute: 'message',
      type: 'text',
      description:
        'The line under the box. Muted helper text on its own; the same line turns red and reads as the ' +
        'reason when `invalid` is also set.',
    },
    {
      name: 'format',
      attribute: 'format',
      type: 'text',
      // CAOS-452 - the four this control actually answers to, named so that a person placing it does
      // not have to go and read the source to find them. `fallback` rather than a bare closed set,
      // because the set is genuinely open: `setInputType` looks the value up and takes 'text' for
      // anything it does not recognise, so nothing is refused here and declaring `options` alone
      // would make it refused - a constraint this control does not enforce becoming one at the
      // deploy gate. The Text vocabulary is wider than these four (a `rich` field is drawn by the
      // rich editor, which is chosen before this control is reached), which is exactly why the
      // honest statement is "these are recognised" and not "these are all there is".
      options: ['long', 'email', 'phone', 'url'],
      fallback: 'plain',
      description:
        'The Text format this field is drawing, named as the kernel names it: long for several lines, and ' +
        'email, phone or url for a single line that asks for the right keyboard and lets the browser check ' +
        'the shape of what is typed. A field in no format is a plain one-line box. The field renderer passes ' +
        'whatever the field declared, so honouring a format is not something a page has to remember to ask for.',
    },
    {
      name: 'maxlength',
      attribute: 'maxlength',
      type: 'number',
      description:
        'The longest value this field accepts, which is the length the field declared. Enforced by the ' +
        'control, so the reader is stopped at the limit rather than having the end of what they typed removed ' +
        'somewhere below. Applies in one line and in several alike. With none declared the field is unbounded.',
    },
    {
      name: 'multiline',
      attribute: 'multiline',
      type: 'boolean',
      description:
        'Draw the value as several lines instead of one. This is the Text kind\'s declared `long` format, ' +
        'not a second field: the label, the value, the validity, the message and every event mean exactly what ' +
        'they mean in one line. A field given format="long" draws several lines whether or not this is also ' +
        'set, so the two cannot disagree; the field renderer sets the format and a page placing this field ' +
        'directly is the only caller that reaches for this instead. The reader can drag the control ' +
        'taller but not wider — a box that can be dragged wider tears the form column out of shape.',
      default: false,
    },
    {
      name: 'inline',
      attribute: 'inline',
      type: 'boolean',
      description:
        'Draw the record-detail variant instead of the boxed form control: no box, an accent underline, ' +
        'inheriting the surrounding type. The label and the message are hidden, because the surrounding ' +
        'record detail supplies the field name.',
      default: false,
    },
  ],

  // Both are plain DOM events rather than CustomEvents, re-dispatched from the shadow root with
  // `composed` so they cross the boundary and a page can listen on the element. Documented because
  // what a page acts on is the event it receives, not the constructor that made it.
  events: [
    { name: 'input', when: 'On every keystroke, after the value attribute has been written back.', carries: 'nothing' },
    { name: 'change', when: 'When the control is committed — focus leaves, or Enter is pressed.', carries: 'nothing' },
  ],

  // The shadow root holds a label, an input and a message div, and no <slot> anywhere. Nothing can
  // be put inside this element.
  // Nothing to read back that cannot also be set. `value` can be read off the element, but a page can
  // set it too, so it is documented as an input rather than here.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-font', controls: 'The typeface of the label, the box and the message.' },
    { token: '--caos-text-body', controls: 'The size of the label and of the typed text.' },
    { token: '--caos-space-1', controls: 'The gap between the label, the box and the message.' },
    { token: '--caos-space-3', controls: 'The vertical padding inside the box.' },
    { token: '--caos-radius', controls: 'The corner radius of the box.' },
    { token: '--caos-color-border', controls: 'The box outline at rest.' },
    { token: '--caos-color-border-strong', controls: 'The box outline while the field has focus.' },
    { token: '--caos-color-surface-2', controls: 'The fill of the box.' },
    { token: '--caos-color-text', controls: 'The colour of the typed text.' },
    { token: '--caos-color-text-muted', controls: 'The label, the placeholder, the message and disabled text.' },
    { token: '--caos-color-accent', controls: 'The hover outline, and the underline of the inline variant.' },
    { token: '--caos-accent-wash', controls: 'The tint that fills the box while it has focus, in place of a focus ring.' },
    { token: '--caos-color-danger', controls: 'The required asterisk, and the outline and message when invalid.' },
  ],

  parts: [
    { part: 'label', is: 'the label above the control' },
    { part: 'input', is: 'the inner text input — or the text area, in the multiline format; one part name, because it is one control' },
    { part: 'message', is: 'the line under the control' },
  ],

  methods: [
    {
      name: 'focus',
      description:
        'Put the caret in the box and select what is already there. The record-detail editor calls it ' +
        'when a value is clicked to edit, so typing replaces rather than appends.',
      args: [
        {
          name: 'options',
          type: 'FocusOptions',
          description: 'The ordinary focus options — pass `{ preventScroll: true }` to focus without scrolling to it.',
        },
      ],
    },
  ],

  placing:
    'Placed as an item in a page section, one per line in a form or one per cell in a record detail. ' +
    'It holds no record binding of its own: which field it is editing comes from the placement, and the ' +
    'page is what reads the value back out and decides whether it is valid.',

  whenToUse:
    'A single line of free text the person types themselves. If the value is one of a known set, or a ' +
    'number, or a date, one of the siblings below is the field that already knows that.',

  insteadUse: [
    {
      name: 'FieldRenderer',
      when: 'The type is not known until the record is read — hand it the field metadata and it draws the right control.',
    },
    { name: 'NumberField', when: 'The value is a number, so it should align in tabular figures and take min/max/step.' },
    { name: 'DateField', when: 'The value is a calendar date and should offer the platform date picker.' },
    { name: 'PicklistField', when: 'The value is one of a fixed list rather than anything the person types.' },
    { name: 'LookupField', when: 'The value points at another record rather than being text of its own.' },
    { name: 'InlineHint', when: 'You only want the explanatory line and there is no value being edited.' },
  ],

  accessibility:
    'The control is a real input with a generated id, and the label points at it, so it is announced with ' +
    'its name and clicking the label focuses the box. `required` and `invalid` are forwarded to the input ' +
    'as `required` and `aria-invalid`. Focus is shown as a wash inside the box rather than a ring, which is ' +
    'the platform treatment. What is left to the author is the label text itself — an unlabelled field is ' +
    'announced as an unnamed text box — and the wording of the message, which is the only thing that says ' +
    'WHY the field is refused.',

  examples: [
    {
      key: 'labelled',
      label: 'Labelled, with a hint',
      summary: 'The ordinary form field: a name above it and a greyed example inside it.',
      placement: {
        component: 'caos-text-field',
        inputs: { label: 'Project name', placeholder: 'e.g. North region rollout' },
      },
    },
    {
      key: 'states',
      label: 'Required, refused, and locked',
      summary:
        'The three states a page sets on a field it is validating, side by side — the asterisk, the red ' +
        'outline with its reason, and a field that may not be typed in at all.',
      placement: [
        { component: 'caos-text-field', inputs: { label: 'Project name', required: true, value: 'North region rollout' } },
        {
          component: 'caos-text-field',
          inputs: { label: 'Reference', invalid: true, value: 'R-1', message: 'A reference is at least four characters.' },
        },
        { component: 'caos-text-field', inputs: { label: 'Order number', disabled: true, value: 'ORD-4471' } },
      ],
    },
  ],
};

export default docs;
